import { useEffect, useRef, useState } from 'react';

/**
 * 色選択スウォッチ (共有 UI、admin / user 双方で利用)。
 *
 * 当初 admin `features/masters/components/ColorSwatch.tsx` にあったものを shared-ui へ昇格。
 * 色指定ボタンを押すと OS の「色の選択」ダイアログ (古いパレット) が開く `<input type="color">`
 * を全面的に置換するための、モダンなポップオーバー型ピッカー。
 *
 *   - 20 色のモダンなプリセットパレット (popover 表示、クリックで onChange)
 *   - HTML5 `<input type="color">` (popover 内の「カスタム」= 任意色ピッカー)
 *   - クリア button (onChange(undefined)、value=null/undefined で空状態)
 *   - 「継承中」ビジュアル: value 未設定時は斜線パターン背景 + 破線枠
 *
 * UI 構成 (横並び):
 *   [スウォッチ] → クリックで popover が下に開く
 *   popover 内: パレット 20 色 grid + custom picker (input[type=color]) + クリア button
 */

/**
 * モダンなプリセットパレット (Tailwind 500 系の彩度を統一した 20 色、虹順 + ニュートラル)。
 * card-print / masters / gis-admin / user 地図設定 など全カラーピッカーで共通利用 (色使いを揃える)。
 */
export const SWATCH_PALETTE: readonly string[] = [
  '#ef4444', // red-500
  '#f97316', // orange-500
  '#f59e0b', // amber-500
  '#eab308', // yellow-500
  '#84cc16', // lime-500
  '#22c55e', // green-500
  '#10b981', // emerald-500
  '#14b8a6', // teal-500
  '#06b6d4', // cyan-500
  '#0ea5e9', // sky-500
  '#3b82f6', // blue-500
  '#6366f1', // indigo-500
  '#8b5cf6', // violet-500
  '#a855f7', // purple-500
  '#d946ef', // fuchsia-500
  '#ec4899', // pink-500
  '#f43f5e', // rose-500
  '#64748b', // slate-500
  '#334155', // slate-700
  '#0f172a', // slate-900
];

export interface ColorSwatchProps {
  /** 現在の色 (#RRGGBB or undefined=継承)。 */
  value: string | undefined;
  /** onChange 時の callback。クリアは undefined を渡す。 */
  onChange: (color: string | undefined) => void;
  /** スウォッチサイズ (default: 28px)。 */
  size?: number;
  /** クリア button を表示 (default: true、undefined を許容する用途向け)。 */
  showClear?: boolean;
  /** a11y label (例: "row 1 color")。 */
  ariaLabel?: string;
  /** 無効化 (trigger を押せなくする)。 */
  disabled?: boolean;
}

export function ColorSwatch({
  value,
  onChange,
  size = 28,
  showClear = true,
  ariaLabel,
  disabled = false,
}: ColorSwatchProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // popover 外 click で閉じる
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  // Escape で popover を閉じる (a11y)
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const isInherited = value === undefined || value === '';
  const styleSwatch: React.CSSProperties = isInherited
    ? {
        width: size,
        height: size,
        background:
          'repeating-linear-gradient(45deg, transparent 0 4px, rgba(100,116,139,0.4) 4px 5px)',
        border: '1px dashed #94a3b8',
        borderRadius: 4,
      }
    : {
        width: size,
        height: size,
        background: value,
        border: '1px solid #cbd5e1',
        borderRadius: 4,
      };

  return (
    <span className="inline-flex items-center gap-1 relative">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={styleSwatch}
        aria-label={ariaLabel ?? (isInherited ? '色を選択 (現在: 継承)' : `色: ${value}`)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
        title={isInherited ? '継承中 (未指定)' : value}
      />
      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="色選択"
          className="absolute top-full left-0 mt-1 z-50 p-2 bg-white border border-slate-300 rounded-md shadow-lg"
          style={{ width: 208 }}
        >
          <div className="grid grid-cols-5 gap-1 mb-2">
            {SWATCH_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                }}
                style={{
                  width: 26,
                  height: 26,
                  background: c,
                  border:
                    value?.toLowerCase() === c.toLowerCase()
                      ? '2px solid #1e293b'
                      : '1px solid #cbd5e1',
                  borderRadius: 4,
                }}
                aria-label={`プリセット色 ${c}`}
                className="cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-400"
                title={c}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
            <label className="flex items-center gap-1 text-[11px] text-slate-600 flex-1">
              カスタム:
              <input
                type="color"
                value={isInherited ? '#888888' : value}
                onChange={(e) => onChange(e.target.value)}
                className="w-8 h-6 border border-slate-300 rounded cursor-pointer"
                aria-label="カスタム色入力"
              />
            </label>
            {showClear && !isInherited && (
              <button
                type="button"
                onClick={() => {
                  onChange(undefined);
                  setOpen(false);
                }}
                className="text-[11px] text-slate-600 hover:text-danger-600 underline"
                aria-label="色をクリア (継承に戻す)"
              >
                クリア
              </button>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
